import { CANVAS_W, CANVAS_H } from "./transform";
import type { ElementTransform } from "./types";

/**
 * A box in uniform canvas pixel space (1920×1080). All gesture math runs here
 * — uniform axes make rotation/resize correct — then converts back to the
 * mixed-axis percentage transform for storage/CSS.
 */
export interface PxBox {
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number; // degrees
}

const MIN_PX = 24;

export function toPx(t: ElementTransform): PxBox {
    return {
        x: (t.x / 100) * CANVAS_W,
        y: (t.y / 100) * CANVAS_H,
        w: (t.width / 100) * CANVAS_W,
        h: (t.height / 100) * CANVAS_H,
        rotation: t.rotation,
    };
}

export function toPct(b: PxBox): ElementTransform {
    return {
        x: (b.x / CANVAS_W) * 100,
        y: (b.y / CANVAS_H) * 100,
        width: (b.w / CANVAS_W) * 100,
        height: (b.h / CANVAS_H) * 100,
        rotation: b.rotation,
    };
}

export type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const rad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Resize a (possibly rotated) box by a canvas-px pointer delta, keeping the
 * opposite corner/edge anchored. Works by projecting the delta into the box's
 * local frame, changing size there, then shifting the center back in world
 * space so the anchor stays put. `keepAspect` locks w/h ratio (corner handles).
 */
export function resizeBox(
    box: PxBox,
    handle: ResizeHandle,
    dxPx: number,
    dyPx: number,
    keepAspect = false
): PxBox {
    const theta = rad(box.rotation);
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);

    // delta → local frame
    const ldx = dxPx * cos + dyPx * sin;
    const ldy = -dxPx * sin + dyPx * cos;

    const signX = handle.includes("e") ? 1 : handle.includes("w") ? -1 : 0;
    const signY = handle.includes("s") ? 1 : handle.includes("n") ? -1 : 0;

    let nw = signX !== 0 ? Math.max(MIN_PX, box.w + signX * ldx) : box.w;
    let nh = signY !== 0 ? Math.max(MIN_PX, box.h + signY * ldy) : box.h;

    if (keepAspect && signX !== 0 && signY !== 0) {
        const ratio = box.w / box.h;
        // drive height from width to keep ratio
        nh = nw / ratio;
    }

    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const localShift = {
        x: (signX * (nw - box.w)) / 2,
        y: (signY * (nh - box.h)) / 2,
    };
    const worldShift = {
        x: localShift.x * cos - localShift.y * sin,
        y: localShift.x * sin + localShift.y * cos,
    };
    const ncx = cx + worldShift.x;
    const ncy = cy + worldShift.y;

    return {
        x: ncx - nw / 2,
        y: ncy - nh / 2,
        w: nw,
        h: nh,
        rotation: box.rotation,
    };
}

/** Snap to multiples of this many degrees when within ROTATE_SNAP_DEG of one. */
const ROTATE_STEP_DEG = 15;
const ROTATE_SNAP_DEG = 5;

/**
 * Rotation (deg) so the box's top points toward the pointer. Magnetically snaps
 * to the nearest 15° when close, unless `disableSnap` (Alt held) is set.
 */
export function rotateBox(
    box: PxBox,
    pointerCanvasX: number,
    pointerCanvasY: number,
    disableSnap = false
): number {
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const angle =
        (Math.atan2(pointerCanvasY - cy, pointerCanvasX - cx) * 180) /
            Math.PI +
        90;
    let rotation = angle % 360;
    if (rotation < 0) rotation += 360;
    if (!disableSnap) {
        const nearest =
            Math.round(rotation / ROTATE_STEP_DEG) * ROTATE_STEP_DEG;
        if (Math.abs(rotation - nearest) <= ROTATE_SNAP_DEG)
            rotation = nearest % 360;
    }
    return rotation;
}

/** Translate a box by a canvas-px delta. */
export function moveBox(box: PxBox, dxPx: number, dyPx: number): PxBox {
    return { ...box, x: box.x + dxPx, y: box.y + dyPx };
}
